import { Grid, InputAdornment, TextField } from '@mui/material';
import {
  GridClearIcon,
  GridColDef,
  GridSearchIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import * as React from 'react';
import { formatDate, formatDateTime, formatNumber, formatPhoneNumber } from './dataFormatter';

const btnSx = {
  backgroundColor: 'transparent',
  color: '#3467FF',
  minWidth: '100px',
  boxShadow: 'none',
  pl: 0,
  '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' },
};
// ---------- styling ----------

// Define a generic CSV formatter type
export type CsvFormatter = (value: unknown) => any;
export const safeString = (value: unknown): string => (value == null ? '' : String(value));
export const defaultCsvFormatters: Record<any, CsvFormatter> = {
  date: (v) => (v == null ? '' : formatDate(String(v))),
  dateTime: (v) => (v == null ? '' : formatDateTime(String(v))),
  phone: (v) => (v == null ? '' : formatPhoneNumber(safeString(v))),
  boolean: (v) => (v == null ? '' : v ? 'Yes' : 'No'),
  number: (v) => (v == null || isNaN(Number(v)) ? '' : formatNumber(Number(v))),
  string: (v) => (v == null ? '' : String(v).toLowerCase()),
};

/**
 * Utility to get formatted CSV value for a given column type.
 * Falls back to `string` formatter if type not found.
 */
export const formatForCsv = (type: string | undefined, value: unknown): string => {
  const formatter = defaultCsvFormatters[type ?? 'string'] || defaultCsvFormatters.string;
  return formatter(value);
};

// ---------- types ----------
type CustomToolbarProps = {
  col?: GridColDef[];
  disableExportList?: boolean;
  disableFilterList?: boolean;
  disableManageCol?: boolean;
  disableSearch?: boolean;
  searchText?: string;
  exportOnlySelected?: boolean;
  onSearch?: (query: string) => void;
  onClear?: () => void;
};

// ---------- toolbar ----------
export const CustomToolbar = React.memo(function CustomToolbar({
  col = [],
  disableExportList = true,
  disableSearch = true,
  disableManageCol = true,
  disableFilterList = true,
  searchText = '',
  exportOnlySelected = true,
  onSearch,
  onClear,
}: CustomToolbarProps) {
  const debounceRef = React.useRef<NodeJS.Timeout>();
  const debouncedOnSearch = (v: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch?.(v), 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    e.preventDefault();
    debouncedOnSearch(v); // parent notified later
  };

  const handleClear = () => {
    onClear?.();
    onSearch?.(''); // immediately clear server/client filters
  };

  const csvColumns = React.useMemo(
    () =>
      col.map((column) => ({
        label: column.headerName ?? column.field,
        value: column.field,
        format: formatForCsv,
      })),
    [col]
  );
  return (
    <GridToolbarContainer sx={{ backgroundColor: 'transparent', mb: '5px' }}>
      {!disableManageCol && <GridToolbarColumnsButton sx={btnSx} size='small' />}
      {!disableFilterList && <GridToolbarFilterButton sx={btnSx} />}
      {!disableExportList && (
        <GridToolbarExport
          csvOptions={{ fileName: 'Organization List', columns: csvColumns }}
          onlySelected={exportOnlySelected}
          printOptions={{ disableToolbarButton: true }}
          sx={btnSx}
        />
      )}

      {!disableSearch && (
        <Grid>
          <TextField
            placeholder='Search'
            sx={{ width: 250, ml: 1 }}
            variant='outlined'
            fullWidth
            margin='normal'
            size='small'
            value={searchText}
            onChange={handleChange}
            // remove focused prop; don't force focus every render
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <GridSearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position='end'>
                  <GridClearIcon onClick={handleClear} sx={{ cursor: 'pointer' }} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
      )}
    </GridToolbarContainer>
  );
});
